import { Project } from '@/models'
import { IProjectService as ProjectService } from '@/services/IProjectService'
import { defineStore } from 'pinia'
import { useBaseStore } from '.'
import { FeaturesService, FeatureResponse as Feature } from '@itemis-solutions/platform-client-sdk'
import { notNullish } from '@vueuse/core'
import { ref, computed } from 'vue'
import { useFeatureFlagStore } from './featureFlagStore'

export const useProjectStore = defineStore('projectStore', () => {
  // State
  const projects = ref<Project[]>([])
  const currentProject = ref<Project | undefined>(undefined)
  const sortCriteria = ref<string>('')
  const filterDate = ref<string>('')
  const filterString = ref<string>('')
  const errorRef = ref<Error | null>(null)

  // Getters (as computed properties)
  const getDateSortedProjects = computed(() => {
    return [...projects.value].sort((a: Project, b: Project) => {
      const favA = a.favorite ? 1 : 0
      const favB = b.favorite ? 1 : 0
      if (favA !== favB) return favB - favA
      const da = new Date(a.creationDate as string)
      const db = new Date(b.creationDate as string)
      return db.getTime() - da.getTime()
    })
  })

  const getProjectById = computed(() => {
    return (id: number) => projects.value.find((project) => project.id === id)
  })

  const knownProjectSlugs = computed(() => {
    return projects.value.map((project) => project.slug).filter(notNullish)
  })

  const isFeatureActive = computed(() => {
    return (feature: string | undefined) => {
      if (feature == undefined) return true
      const override = useFeatureFlagStore().getOverride(feature)
      if (override !== null) return override
      return !!currentProject.value?.features?.find((f) => f.fqn === feature)
    }
  })

  // Actions (as functions)
  async function loadProjectsFromApi(projectService: ProjectService) {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    if (projectService !== undefined) {
      try {
        const response = await projectService.getProjects()
        errorRef.value = null
        setProjects(response)
      } catch (error: any) {
        errorRef.value = error
        console.error('Error loading projects:', error)
      } finally {
        baseStore.decrementRunningJobs()
      }
    }
  }

  async function loadProjectsOfOrganizationFromApi(
    projectService: ProjectService,
    organizationIdOrSlug: string,
  ) {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const response = await projectService.getProjectsOfOrganization(organizationIdOrSlug)
      errorRef.value = null
      setProjects(response)
    } catch (error: any) {
      errorRef.value = error
      console.error('Error loading organization projects:', error)
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  function addNewProject(project: Project) {
    projects.value.push(project)
  }

  async function removeProject(projectService: ProjectService, project: Project) {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const data = await projectService.deleteProject(project.id)
      const projectIndex = projects.value.findIndex((item) => item.id === data.id)
      if (projectIndex > -1) {
        projects.value.splice(projectIndex, 1)
      }
    } catch (error) {
      console.error('Error removing project:', error)
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  async function putProject(projectService: ProjectService, project: Project) {
    const baseStore = useBaseStore()
    baseStore.startNewJob()
    try {
      const updatedProject = await projectService.putProject(project)
      updateProject(updatedProject)
      setCurrentProject(updatedProject)
      return updatedProject
    } catch (error) {
      console.error('Error updating project:', error)
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  function setProjectAsFavorite(item: Project) {
    projects.value.forEach((project) => {
      if (project.name === item.name && project.id === item.id) {
        project.favorite = item.favorite
      }
    })
  }

  async function getFeaturesForProject(project: Project): Promise<Feature[]> {
    if (project && project.resourceId) {
      const { data } = await FeaturesService.getAvailableFeaturesWithDetails({
        itemisResourceId: project.resourceId,
      })
      if (currentProject.value) {
        currentProject.value.features = data
      }
      return data
    }
    return []
  }

  function setProjects(newProjects: Project[]) {
    projects.value = newProjects
  }

  function updateProject(updatedProject: Project) {
    const index = projects.value.findIndex((project) => project.id === updatedProject.id)
    if (index !== -1) {
      projects.value.splice(index, 1, updatedProject)
    }
  }

  async function setCurrentProject(project: Project | undefined) {
    if (project) {
      if (project.resourceId) {
        getFeaturesForProject(project)
      } else {
        project.features = undefined
      }
    }
    currentProject.value = project
  }

  function setSortCriteria(newSortCriteria: string) {
    sortCriteria.value = newSortCriteria
  }

  function setFilterDate(date: string) {
    filterDate.value = date
  }

  function setFilterString(filter: string) {
    filterString.value = filter
  }

  // Return public API
  return {
    // State
    projects,
    currentProject,
    errorRef,
    sortCriteria,
    filterDate,
    filterString,
    // Getters
    getDateSortedProjects,
    getProjectById,
    knownProjectSlugs,
    isFeatureActive,
    // Actions
    loadProjectsFromApi,
    loadProjectsOfOrganizationFromApi,
    addNewProject,
    removeProject,
    putProject,
    setProjectAsFavorite,
    getFeaturesForProject,
    setProjects,
    updateProject,
    setCurrentProject,
    setSortCriteria,
    setFilterDate,
    setFilterString,
  }
})
